<script>
  import Button from '@leaflink/stash/Button.vue';
  import IconLabel from '@leaflink/stash/IconLabel.vue';
  import { storeToRefs } from 'pinia';

  import { CHILI_PIPER_URL } from '@/constants';
  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { track } from '@/track';
  import { loadScript } from '@/utils/files';

  import JoinTodayBuyerDirect from './components/JoinTodayBuyerDirect.vue';
  import JoinTodayDigitalOnboarding from './components/JoinTodayDigitalOnboarding.vue';
  import JoinTodaySellerDirect from './components/JoinTodaySellerDirect.vue';

  export default {
    name: 'JoinToday',

    components: {
      Button,
      IconLabel,
      JoinTodayDigitalOnboarding,
      JoinTodayBuyerDirect,
      JoinTodaySellerDirect,
    },

    data() {
      return {
        CHILI_PIPER_ROUTER: 'payments-router',
      };
    },

    computed: {
      buyerContents() {
        return {
          headline: 'Buy Now, Pay Later',
          leader: [
            'With LeafLink Payments, you can order more, pay later, and keep more cash on hand. This means reducing your procurement expenses and investing back into the growth of your business.',
          ],
          buttonContent: "I'm Interested",
          body: [
            {
              text: '<b>Take advantage of net payment terms (e.g. Net 30, 45, 60) and payment extension offers</b> so you can sell through your inventory before paying for it. ',
            },
            {
              text: '<b>Centralize your Accounts Payable</b> on LeafLink and reduce the volume of payees and payments to manage – increasing your team’s speed and efficiency. ',
            },
            {
              text: '<b>Increase your purchasing power</b> with an approved purchasing line of credit – secure a lower cost of goods, drive sales growth, and avoid stock outs.',
            },
            {
              text: '<b>Process payments easily and digitally</b> in a manner that is both reliable and secure via our trusted third party financial institutions and partners.',
            },
            {
              text: '<b>Optimize cash flow, gain greater financial flexibility</b>, and invest back in the growth of your business',
            },
          ],
        };
      },

      sellerContents() {
        return {
          headline: 'Receive digital payments quickly and securely',
          leader: [
            'Power your business forward with a B2B payment solution that pays you upfront and protects you from risk. We’ll help you get paid faster, so you can gain access to working capital and streamline your entire accounting process, all from a single platform.',
          ],
          buttonContent: "I'm Interested",
          body: [
            {
              text: '<b>Get paid upon delivery confirmation</b> and let LeafLink assume the risk of non-payment – you are not liable for unpaid invoices.',
            },
            {
              text: '<b>Automate and centralize your Accounts Receivable</b> so you can increase speed and efficiency.',
            },
            {
              text: '<b>Speed up your time-to-cash</b> and allocate the working capital into other facets of the business.',
            },
            {
              text: '<b>Process payments that are both reliable and secure</b>, and backed by trusted financial institutions.',
            },
            { text: '<b>Generate more sales</b> by giving your customers more purchasing power.' },
          ],
        };
      },

      contents() {
        return this.activeCompany.buyer ? this.buyerContents : this.sellerContents;
      },

      pageType() {
        return this.$route.query.type;
      },

      buttonContent() {
        return this.contents.buttonContent || 'Join today';
      },

      headline() {
        return this.contents.headline || 'Let LeafLink power your payments';
      },

      leader() {
        return this.contents.leader || [];
      },

      subhead() {
        return this.contents.subhead;
      },
    },

    watch: {
      activeCompany: {
        handler() {
          if (this.activeCompany?.enableChiliPiperForState) {
            loadScript(CHILI_PIPER_URL);
          }
        },
        immediate: true,
      },
    },

    setup() {
      const companyStore = useCompanyStore();
      const { activeCompany } = storeToRefs(companyStore);

      const { getIsSplitEnabled, user } = storeToRefs(useUserStore());

      return {
        activeCompany: activeCompany,
        isSplitEnabled: getIsSplitEnabled,
        user,
      };
    },

    methods: {
      onClick() {
        track('Clicked Join Payments', {
          user_id: this.user.id,
          company_id: this.activeCompany.id,
          SFCID: '7018W000000xUfeQAE',
        });

        if (this.activeCompany.enableChiliPiperForState) {
          this.callChiliPiper();
        }

        return true;
      },

      callChiliPiper() {
        const cp = window.ChiliPiper;

        if (cp) {
          cp.submit('leaflink', this.CHILI_PIPER_ROUTER, {
            map: true,
            debug: true,
            lead: {
              FirstName: this.user.firstName,
              LastName: this.user.lastName,
              Email: this.user.email,
              AccountID: this.activeCompany.salesforceId,
              Phone: this.user.phone,
              State: this.activeCompany.state,
              IsBuyer: this.activeCompany.buyer,
              IsSeller: this.activeCompany.seller,
            },
          });
        }
      },
    },
  };
</script>

<template>
  <JoinTodayBuyerDirect
    v-if="activeCompany && pageType === 'buyer-direct'"
    :company="activeCompany"
    @cta-click="onClick"
  />

  <JoinTodaySellerDirect v-else-if="pageType === 'seller-direct'" @cta-click="onClick" />

  <JoinTodayDigitalOnboarding v-else-if="activeCompany" :company="activeCompany" @cta-click="onClick" />

  <div v-else>
    <div class="container" :class="[$style.wrapper]">
      <h1 v-html="headline" :class="[$style.copy, $style.headline]"></h1>
      <h3 class="color-ice-700" :class="[$style.copy]">{{ subhead }}</h3>
      <p v-for="(l, index) in leader" :key="index" class="color-ice-700" :class="[$style.copy, $style.leader]">
        {{ l }}
      </p>
      <div class="ll-my-6 text-center">
        <Button @click="onClick">
          {{ buttonContent }}
        </Button>
      </div>

      <ul class="flex flex-wrap align-center text-left" :class="[$style.copy, $style['contents-body']]">
        <li v-for="(content, index) in contents.body" :key="index">
          <IconLabel icon="check" color="blue-500">
            <h4 v-if="content.header" v-html="content.header" class="ll-mb-1"></h4>
            <p v-html="content.text"></p>
          </IconLabel>
        </li>
      </ul>
    </div>

    <div class="container ll-mt-4" :class="$style.wrapper">
      <img :src="'/static/img/brands-page-hero.png'" alt="" class="w-full" :class="$style.image" />
    </div>
  </div>
</template>

<style lang="scss" module>
  $max-copy-width: 800px;

  .wrapper {
    padding: space(3);
  }

  .copy {
    margin-left: auto;
    margin-right: auto;
    max-width: $max-copy-width;
    text-align: center;
  }

  .headline {
    font-size: 2.875rem;
    letter-spacing: -0.02em;
    max-width: 930px;
  }

  .leader {
    font-size: 1rem;
    margin-top: space(2);
  }

  .contents-body {
    max-width: 1100px;

    > li {
      padding: space(2) 0;
      width: 100%;

      b {
        color: var(--color-ice-900);
        font-weight: 800;
      }

      @include breakpoint('md') {
        max-width: 50%;
        padding: space(2) space(3);
      }

      @include breakpoint('lg') {
        max-width: 33%;
      }
    }
  }

  .image {
    margin-left: auto;
    margin-right: auto;
    max-width: $max-copy-width;
  }
</style>
